import { CalendarEvent, Member } from '../types/calendar';
import { getEventDayPortion } from './event-utils';
import { deduplicateEvents, DeduplicatedEvent } from './event-deduplication';

interface EventWithPosition {
  event: CalendarEvent;
  top: number;
  bottom: number;
}

export interface EventLaneAssignment {
  eventsWithLanes: Record<string, Array<CalendarEvent & { laneIndex: number; totalLanes: number }>>;
  overflowCounts: Record<string, number>;
}

export interface CrossMemberLaneAssignment {
  events: Array<DeduplicatedEvent & { laneIndex: number; totalLanes: number }>;
  overflowCount: number;
}

/**
 * Assign events to horizontal lanes for side-by-side display (per member).
 * Events are grouped into "collision clusters" — connected by overlap or near-adjacency
 * (within ADJACENCY_GAP_PX). Each cluster gets its own lane count so isolated events
 * stay full-width while dense timetable blocks spread into readable columns.
 */
export function assignEventLanes(
  members: Member[],
  dayEvents: Record<string, CalendarEvent[]>,
  date: Date,
  getEventStyle: (event: CalendarEvent, currentDate: Date) => { top: number; height: number } | null
): EventLaneAssignment {
  const MAX_VISIBLE_LANES = 3;
  // Treat events within 8px (~8min) of each other as "adjacent" for column layout.
  // This makes back-to-back school timetable slots (e.g. 8:30 end → 8:40 start) spread into columns.
  const ADJACENCY_GAP_PX = 8;
  const result: Record<string, Array<CalendarEvent & { laneIndex: number; totalLanes: number }>> = {};
  const overflows: Record<string, number> = {};

  for (const member of members) {
    const memberEvents = (dayEvents[member.id] || []).filter((e: CalendarEvent) => {
      if (e.allDay) return false;
      const portion = getEventDayPortion(e, date);
      return !portion?.isMiddleDay;
    });

    // Calculate positions
    const eventsWithTimes: EventWithPosition[] = memberEvents.map((event: CalendarEvent) => {
      const style = getEventStyle(event, date);
      return {
        event,
        top: style?.top ?? 0,
        bottom: (style?.top ?? 0) + (style?.height ?? 0),
      };
    });

    // Sort by start time, then duration DESC
    eventsWithTimes.sort((a, b) => {
      if (a.top !== b.top) return a.top - b.top;
      return (b.bottom - b.top) - (a.bottom - a.top);
    });

    // Step 1: Group events into collision clusters.
    // Two events are in the same cluster if one starts within ADJACENCY_GAP_PX of
    // the other ending (or they actually overlap).
    const clusters: EventWithPosition[][] = [];
    let currentCluster: EventWithPosition[] = [];
    let clusterEnd = -Infinity;

    for (const item of eventsWithTimes) {
      if (currentCluster.length === 0 || item.top <= clusterEnd + ADJACENCY_GAP_PX) {
        // Extends the current cluster
        currentCluster.push(item);
        clusterEnd = Math.max(clusterEnd, item.bottom);
      } else {
        clusters.push(currentCluster);
        currentCluster = [item];
        clusterEnd = item.bottom;
      }
    }
    if (currentCluster.length > 0) clusters.push(currentCluster);

    // Step 2: Assign lanes within each cluster independently
    const allAssigned: Array<EventWithPosition & { laneIndex: number; totalLanes: number }> = [];
    let totalOverflow = 0;

    for (const cluster of clusters) {
      const lanes: EventWithPosition[][] = [];
      const clusterAssigned: Array<EventWithPosition & { laneIndex: number }> = [];

      for (const item of cluster) {
        let assignedLane = -1;

        for (let i = 0; i < Math.min(lanes.length, MAX_VISIBLE_LANES); i++) {
          const hasOverlap = (lanes[i] ?? []).some(
            (existing) => item.top < existing.bottom + ADJACENCY_GAP_PX && existing.top < item.bottom + ADJACENCY_GAP_PX
          );
          if (!hasOverlap) {
            assignedLane = i;
            break;
          }
        }

        if (assignedLane === -1 && lanes.length < MAX_VISIBLE_LANES) {
          assignedLane = lanes.length;
          lanes.push([]);
        }

        if (assignedLane === -1) {
          totalOverflow++;
          continue;
        }

        lanes[assignedLane]!.push(item);
        clusterAssigned.push({ ...item, laneIndex: assignedLane });
      }

      const clusterLanes = Math.max(Math.min(lanes.length, MAX_VISIBLE_LANES), 1);
      for (const a of clusterAssigned) {
        allAssigned.push({ ...a, totalLanes: clusterLanes });
      }
    }

    result[member.id] = allAssigned.map((a) => ({
      ...a.event,
      laneIndex: a.laneIndex,
      totalLanes: a.totalLanes,
    }));
    overflows[member.id] = totalOverflow;
  }

  return { eventsWithLanes: result, overflowCounts: overflows };
}

/**
 * Assign events to lanes across all members based on actual time overlap.
 * This is used by DayView to only create separate columns when events truly overlap,
 * regardless of which family member they belong to.
 * 
 * Important: Events that end exactly when another starts (e.g., A ends 10:00, B starts 10:00)
 * are NOT considered overlapping - they should be stacked vertically in a single column.
 */
export function assignCrossMemberLanes(
  members: Member[],
  dayEvents: Record<string, CalendarEvent[]>,
  date: Date,
  getEventStyle: (event: CalendarEvent, currentDate: Date) => { top: number; height: number } | null
): CrossMemberLaneAssignment {
  const MAX_VISIBLE_LANES = 3;

  // Deduplicate events across members (e.g., same meeting invited to Sidney & Mabel)
  const allEvents = deduplicateEvents(dayEvents, members);

  // Filter to timed events only (exclude all-day and middle days of multi-day events)
  const timedEvents = allEvents.filter((e) => {
    if (e.allDay) return false;
    const portion = getEventDayPortion(e, date);
    return !portion?.isMiddleDay;
  });

  // Calculate positions for all events
  const eventsWithTimes = timedEvents
    .map((event) => {
      const style = getEventStyle(event, date);
      if (!style) return null;
      return {
        event,
        top: style.top,
        bottom: style.top + style.height,
      };
    })
    .filter((e): e is { event: DeduplicatedEvent; top: number; bottom: number } => e !== null);

  // Sort by start time, then by duration DESC (longer events first for better packing)
  eventsWithTimes.sort((a, b) => {
    if (a.top !== b.top) return a.top - b.top;
    const aDuration = a.bottom - a.top;
    const bDuration = b.bottom - b.top;
    return bDuration - aDuration;
  });

  // Greedy lane assignment - find first available lane or create new one
  const lanes: Array<Array<{ event: DeduplicatedEvent; top: number; bottom: number }>> = [];
  const assignedEvents: Array<{ event: DeduplicatedEvent; laneIndex: number }> = [];
  let overflowCount = 0;

  for (const item of eventsWithTimes) {
    let assignedLane = -1;

    // Try to find an existing lane where this event fits
    for (let laneIdx = 0; laneIdx < Math.min(lanes.length, MAX_VISIBLE_LANES); laneIdx++) {
      const lane = lanes[laneIdx];
      if (!lane) continue;

      // Check for actual overlap (strict - touching edges is NOT overlap)
      // eventA.end > eventB.start && eventA.start < eventB.end
      const hasOverlap = lane.some(
        (existing) => item.top < existing.bottom && existing.top < item.bottom
      );

      if (!hasOverlap) {
        assignedLane = laneIdx;
        break;
      }
    }

    // Create new lane if needed (up to MAX_VISIBLE_LANES)
    if (assignedLane === -1 && lanes.length < MAX_VISIBLE_LANES) {
      assignedLane = lanes.length;
      lanes.push([]);
    }

    // If still no lane, this is overflow
    if (assignedLane === -1) {
      overflowCount++;
      continue;
    }

    // Assign event to lane
    lanes[assignedLane]!.push(item);
    assignedEvents.push({ event: item.event, laneIndex: assignedLane });
  }

  const totalLanes = Math.max(lanes.length, 1);

  return {
    events: assignedEvents.map((a) => ({
      ...a.event,
      laneIndex: a.laneIndex,
      totalLanes,
    })),
    overflowCount,
  };
}